import React from "react";
import "./MetricModal.css";

import "./AnalyticsDashboard.css";

const MetricModal = ({ 
  isOpen, 
  onClose, 
  title, 
  icon, 
  dateRange,
  onDateRangeChange,
  showCustomInput,
  showMonthInput,
  showYearInput,
  customDays,
  customMonth,
  customYear,
  onCustomDaysChange,
  onCustomMonthChange,
  onCustomYearChange,
  onCustomDaysSubmit,
  onCustomMonthSubmit,
  onCustomYearSubmit,
  onCloseCustomInput,
  children,
  selectedPlatform,
  onPlatformChange,
  showPlatformSelector, 
}) => {
  // Close modal on Escape key press
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Helper function to get display text for custom ranges
  // Get display label for current selection
  const getCurrentRangeLabel = () => {
    if (dateRange.startsWith("custom-")) {
      const days = dateRange.split("-")[1];
      return `Last ${days} Day${days === "1" ? "" : "s"}`;
    } else if (dateRange.startsWith("month-")) {
      const months = dateRange.split("-")[1];
      return `Last ${months} Month${months === "1" ? "" : "s"}`;
    } else if (dateRange.startsWith("year-")) {
      const year = dateRange.split("-")[1];
      return `Year ${year}`;
    }
    
    const rangeLabels = {
      "30min": "Last 30 Minutes",
      "24h": "Last 24 Hours",
      "7": "Last 7 Days",
      "10": "Last 10 Days",
      "30": "Last 30 Days",
      "90": "Last 90 Days",
      "1_month": "Last Month",
      "3_months": "Last 3 Months",
      "6_months": "Last 6 Months",
      "8_months": "Last 8 Months",
      "past_year": "Past Year",
      "ytd": "Year to Date",
    };
    
    return rangeLabels[dateRange] || "Select Range";
  };

  return (
    <div className="metric-modal-overlay" onClick={handleOverlayClick}>
      <div className="metric-modal-container">
        {/* Modal Header */}
        <div className="metric-modal-header">
          <div className="metric-modal-title-section">
            <span className="metric-modal-icon">{icon}</span>
            <h2 className="metric-modal-title">{title}</h2>
            {/* Display current selection as a badge */}
              <span className="metric-modal-current-range">
                {getCurrentRangeLabel()}
              </span>
          </div>
          
          {/* Date Range Selector */}
          <div className="header-controls">
            <div className="date-range-container">
              {/* Platform Selector - Show only for daily_active_users */}
              {showPlatformSelector && (
                <select
                  className="date-range-selector"
                  value={selectedPlatform}
                  onChange={onPlatformChange}
                  style={{ marginRight: '8px' }}
                >
                  <option value="ALL_PLATFORMS">All Platforms</option>
                  <option value="IOS">iOS</option>
                  <option value="ANDROID">Android</option>
                  <option value="WEB">Web</option>
                </select>
              )}
              <select
                className="date-range-selector"
                value={
                  dateRange.startsWith("custom-") ||
                  dateRange.startsWith("year-") ||
                  dateRange.startsWith("month-")
                    ? dateRange.startsWith("custom-")
                      ? "custom"
                      : dateRange.startsWith("year-")
                      ? "year"
                      : "month"
                    : dateRange
                }
                onChange={onDateRangeChange}
              >
                <optgroup label="Real-time">
                  <option value="30min">Last 30 Minutes</option>
                  <option value="24h">Last 24 Hours</option>
                </optgroup>
                <optgroup label="Days">
                  <option value="7">Last 7 Days</option>
                  <option value="10">Last 10 Days</option>
                  <option value="30">Last 30 Days</option>
                  <option value="90">Last 90 Days</option>
                  <option value="custom">Custom Days...</option>
                </optgroup>
                <optgroup label="Months">
                  <option value="1_month">Last Month</option>
                  <option value="3_months">Last 3 Months</option>
                  <option value="6_months">Last 6 Months</option>
                  <option value="8_months">Last 8 Months</option>
                  <option value="month">Custom Months...</option>
                </optgroup>
                <optgroup label="Year">
                  <option value="past_year">Past Year</option>
                  <option value="ytd">Year to Date</option>
                  {/* <option value="year">Specific Year...</option> */}
                </optgroup>
              </select>
              {/* Show "Edit" button when custom value is active */}
              {/* {(dateRange.startsWith("custom-") || 
                dateRange.startsWith("month-") || 
                dateRange.startsWith("year-")) && (
                <button
                  className="modal-edit-custom-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    // Trigger the custom input based on current type
                    const event = {
                      target: {
                        value: dateRange.startsWith("custom-") 
                          ? "custom" 
                          : dateRange.startsWith("month-")
                          ? "month"
                          : "year"
                      }
                    };
                    onDateRangeChange(event);
                  }}
                  title="Edit custom range"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                </button>
              )} */}

              {showCustomInput && (
                <>
                  <div
                    className="custom-input-overlay"
                    onClick={onCloseCustomInput}
                  />
                  <div className="custom-input-container">
                    <input
                      type="number"
                      className="custom-input"
                      placeholder="Enter days"
                      min="1"
                      max="365"
                      value={customDays}
                      onChange={onCustomDaysChange}
                      onKeyPress={(e) => e.key === "Enter" && onCustomDaysSubmit()}
                    />
                    <button
                      className="apply-btn"
                      onClick={onCustomDaysSubmit}
                      disabled={!customDays || parseInt(customDays) <= 0}
                    >
                      Apply
                    </button>
                  </div>
                </>
              )}

              {showMonthInput && (
                <>
                  <div
                    className="custom-input-overlay"
                    onClick={onCloseCustomInput}
                  />
                  <div className="custom-input-container">
                    <input
                      type="number"
                      className="custom-input"
                      placeholder="Enter months"
                      min="1"
                      max="60"
                      value={customMonth}
                      onChange={onCustomMonthChange}
                      onKeyPress={(e) => e.key === "Enter" && onCustomMonthSubmit()}
                    />
                    <button
                      className="apply-btn"
                      onClick={onCustomMonthSubmit}
                      disabled={!customMonth || parseInt(customMonth) <= 0}
                    >
                      Apply
                    </button>
                  </div>
                </>
              )}

              {showYearInput && (
                <>
                  <div
                    className="custom-input-overlay"
                    onClick={onCloseCustomInput}
                  />
                  <div className="custom-input-container">
                    <input
                      type="number"
                      className="custom-input"
                      placeholder="Enter year"
                      min="2020"
                      max={new Date().getFullYear()}
                      value={customYear}
                      onChange={onCustomYearChange}
                      onKeyPress={(e) => e.key === "Enter" && onCustomYearSubmit()}
                    />
                    <button
                      className="apply-btn"
                      onClick={onCustomYearSubmit}
                      disabled={
                        !customYear ||
                        parseInt(customYear) < 2020 ||
                        parseInt(customYear) > new Date().getFullYear()
                      }
                    >
                      Apply
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          <button
            className="metric-modal-close-btn"
            onClick={onClose}
            aria-label="Close modal"
          >
            ✕
          </button>
        </div>

        {/* Modal Content */}
        <div className="metric-modal-content">{children}</div>
      </div>
    </div>
  );
};

export default MetricModal;